import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react"
import { EntityId, FulfillmentTypes } from "@jackfruit/common"
import React from "react"
import { useTranslation } from "react-i18next"
import { useCart } from "~/hooks/useCart"
import { usePrintService } from "~/hooks/usePrintService"

interface TaxProps {
  taxInclusive: boolean
  taxTotalFloat: number
  taxTotalHuman: string
  fulfillment?: FulfillmentTypes
  printServiceId?: EntityId
}

const TaxRow: React.FC<TaxProps> = ({
  taxInclusive,
  taxTotalFloat,
  taxTotalHuman,
  fulfillment,
  printServiceId,
}) => {
  const { t } = useTranslation()
  const { cart } = useCart()
  const { fulfillment: cartFulfillment } = cart
  const { printService } = usePrintService(printServiceId ?? 0)

  const currentFulfillment = fulfillment ?? cartFulfillment

  const hideTaxLine = !taxTotalFloat || !printService?.displayTax

  if (hideTaxLine) {
    return <Tr />
  }

  return (
    <Tr>
      <Td paddingX={0} paddingY={2}>
        <HStack spacing={1} alignItems="baseline">
          <Text fontWeight="bold">
            {t("components.checkout.OrderSummaryTotals.TaxRow.Tax")}
          </Text>
          {taxInclusive && (
            <Text fontSize={10}>
              {currentFulfillment === "pickup" &&
                t(
                  "components.checkout.OrderSummaryTotals.TaxRow.CalculatedInStore"
                )}
            </Text>
          )}
        </HStack>
      </Td>
      <Td paddingX={0} paddingY={2}>
        <Text textAlign="right">{taxTotalHuman}</Text>
      </Td>
    </Tr>
  )
}

export const SummaryTotals = () => {}

interface OrderSummaryProps {
  freightFloat: number
  freightHuman: string
  taxInclusive: boolean
  taxTotalFloat: number
  taxTotalHuman: string
  totalHuman: string
  subtotalHuman?: string
  discountFloat: number
  discountHuman: string
  printServiceId?: EntityId
  couponCode?: string
  fulfillment?: FulfillmentTypes
  isUpdating?: boolean
  isPendingAddress?: boolean
  serviceFee?: string
  serviceFeeFloat?: number
  serviceFeeHuman?: string
}

const OrderSummaryTotals: React.FC<OrderSummaryProps> = ({
  freightFloat,
  freightHuman,
  taxInclusive,
  taxTotalFloat,
  taxTotalHuman,
  totalHuman,
  subtotalHuman,
  couponCode,
  discountFloat,
  discountHuman,
  fulfillment,
  printServiceId,
  isUpdating = false,
  isPendingAddress = false,
  serviceFee,
  serviceFeeFloat,
  serviceFeeHuman,
}) => {
  const { t } = useTranslation()

  const showCouponCodeRow = Boolean(couponCode) && Boolean(discountFloat)
  const showServiceFeeRow = Boolean(serviceFee) && Boolean(serviceFeeFloat)

  const couponCodeRow = (
    <Tr>
      <Td paddingX={0} paddingY={2}>
        <HStack spacing={1} alignItems="baseline">
          <Text fontWeight="bold">
            {t("components.checkout.OrderSummaryTotals.Discount")}
          </Text>
          <Text fontSize={10}>{couponCode}</Text>
        </HStack>
      </Td>
      <Td paddingX={0} paddingY={2}>
        <Text textAlign="right" color="red.500">
          -{discountHuman}
        </Text>
      </Td>
    </Tr>
  )

  return (
    <Box mt={4}>
      <Table
        size="md"
        colorScheme="whiteAlpha"
        mt={-2}
        data-testid="order-summary-totals-tbl"
      >
        <Tbody>
          {Boolean(freightFloat) && (
            <Tr>
              <Td paddingX={0} paddingY={2}>
                <HStack spacing={1} alignItems="baseline">
                  <Text fontWeight="bold">
                    {t("components.checkout.OrderSummaryTotals.Shipping")}
                  </Text>
                  {isPendingAddress && (
                    <Text fontSize={10}>
                      {t(
                        "components.checkout.OrderSummaryTotals.ShippingPending"
                      )}
                    </Text>
                  )}
                </HStack>
              </Td>
              <Td paddingX={0} paddingY={2}>
                <Text textAlign="right">{freightHuman}</Text>
              </Td>
            </Tr>
          )}

          <TaxRow
            taxInclusive={taxInclusive}
            taxTotalFloat={taxTotalFloat}
            taxTotalHuman={taxTotalHuman}
            fulfillment={fulfillment}
            printServiceId={printServiceId}
          />
          {showCouponCodeRow && couponCodeRow}

          <Tr>
            <Td colSpan={2} paddingX={0} paddingY={0}>
              <hr />
            </Td>
          </Tr>

          <Tr>
            <Td paddingX={0} paddingY={2}>
              <HStack spacing={1} align="baseline">
                <Text fontWeight="bold">
                  {t("components.checkout.OrderSummaryTotals.Total")}
                </Text>
                {taxInclusive && (
                  <Text fontSize={10}>
                    {t("components.checkout.OrderSummaryTotals.TaxInclusive")}
                  </Text>
                )}
              </HStack>
            </Td>
            <Td paddingX={0} paddingY={2} textAlign="right">
              {isUpdating && <Spinner size="sm" color="primary.400" />}
              {!isUpdating && <Text fontWeight="bold">{totalHuman}</Text>}
            </Td>
          </Tr>
          {showServiceFeeRow && (
            <>
              <Tr>
                <Td colSpan={2} paddingX={0} paddingY={0}>
                  <hr />
                </Td>
              </Tr>
              <Tr>
                <Td paddingX={0} paddingY={2}>
                  <Text fontWeight="bold">
                    {t("components.checkout.OrderSummaryTotals.DueNow")}
                  </Text>
                </Td>
                <Td paddingX={0} paddingY={2} textAlign="right">
                  <Text fontWeight="bold">{serviceFeeHuman}</Text>
                </Td>
              </Tr>
              <Tr>
                <Td paddingX={0} paddingY={2}>
                  {t("components.checkout.OrderSummaryTotals.PayOnPickup")}
                </Td>
                <Td paddingX={0} paddingY={2} textAlign="right">
                  <Text>{subtotalHuman}</Text>
                </Td>
              </Tr>
            </>
          )}
        </Tbody>
      </Table>
      {showServiceFeeRow && (
        <Accordion borderWidth={0} borderColor="transparent" allowToggle mt={3}>
          <AccordionItem borderWidth={0} borderBottomWidth={0}>
            <AccordionButton
              paddingX={0}
              _hover={{ bg: "transparent" }}
              borderWidth={0}
            >
              <Flex
                justifyContent="space-between"
                width="100%"
                textAlign="left"
              >
                <Text fontSize="sm" fontWeight="bold" color="gray.500">
                  {t("components.checkout.OrderSummaryTotals.WhyPayADeposit")}
                </Text>
                <AccordionIcon />
              </Flex>
            </AccordionButton>
            <AccordionPanel paddingX={0} py={2} borderBottomWidth={0}>
              <Text fontSize="sm">
                {t(
                  "components.checkout.OrderSummaryTotals.WhyPayADepositDescription"
                )}
              </Text>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Box>
  )
}

export default OrderSummaryTotals
